import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PrintedCardCover } from '../../models/serializable/pass-template/printed-card-cover.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class PrintedCardCoverComponent extends Component {
  static readonly TYPE_ID: string = 'a2c228082cf24ff3bb3c1679e9d34be0';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(public readonly cover: PrintedCardCover) {super();}

  get $typeId(): string {
    return PrintedCardCoverComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return true;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(PrintedCardCoverComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PrintedCardCoverComponent {
    return new PrintedCardCoverComponent(
        context.fromData<PrintedCardCover>(MapUtils.getRaw(map, 'cover') as SerializedData),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'cover': context.toData(this.cover),
    };
  }

  clone(): this {
    return new PrintedCardCoverComponent(
        this.cover.clone(),
    ) as this;
  }

  isSame(other: PrintedCardCoverComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.cover, this.cover);
  }
}

export class PrintedCardCoverComponentBuilder extends DataModelBuilder<PrintedCardCoverComponent> {
  build(context: SerializationContext, map: SerializedData): PrintedCardCoverComponent {
    return PrintedCardCoverComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(PrintedCardCoverComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(PrintedCardCoverComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - parameters `front` and `back` of type [PhotoReference | null] was
     *   replaced with parameter `cover` of type [PrintedCardCover]
     */
    if(version < 2) {
      const cover = PrintedCardCover.fromMap(context, map);
      map['cover'] = context.toData(cover);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: PrintedCardCoverComponent,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? PrintedCardCoverComponent.SCHEMA_VERSION;

    /**
     * Version 1:
     * - parameter `cover` of type [PrintedCardCover] was replaced with
     *   parameters `front` and `back` of type [PhotoReference | null]
     */
    if(version >= 2) {
      version = 1;

      map['front'] = context.toDataOrNull(model.cover.front);
      map['back'] = context.toDataOrNull(model.cover.back);
      delete map['cover'];
      map['$schema_version'] = VersionRange.encodedExactly(1);
    }

    return map;
  }
}
